import { useState, useMemo } from 'react';
import { useCashflow } from '../context/CashflowContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ExpenseFormDialog from '../components/ExpenseFormDialog';
import ArrearsCell from '../components/ArrearsCell';
import {
  TrendingUp,
  Calendar,
  CheckCircle2,
  Clock,
  Building2,
  User,
  Plus,
  Wallet,
  Download,
  FileText,
  History,
  Receipt,
  Pencil,
  AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { forecast, expenses, categories, activeCashflow, loading, toggleExpense, updateExpense, markExpensePaid, unmarkExpensePaid, balances, recordBalanceEntry } = useCashflow();

  // Server-side running balance (ledger) keyed by expense id. This is the single
  // source of truth for outstanding/arrears — the legacy dashboard_state
  // running_balances blob is no longer read for display.
  const balanceByExpense = useMemo(() => {
    const m = {};
    (balances?.expenses || []).forEach((b) => { m[b.expense_id] = b; });
    return m;
  }, [balances]);

  const [expenseFilter, setExpenseFilter] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [showExport, setShowExport] = useState(false);
  // Which forecast month (0 = this month) the printable monthly report covers.
  const [reportMonth, setReportMonth] = useState('0');
  const [editingExpense, setEditingExpense] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    amount: '',
    category_id: '',
    expense_type: 'personal',
    frequency: 'monthly',
    due_date: new Date(),
    end_date: null,
    notes: '',
    gst_inclusive: false,
  });

  // Occurrence keys ("expenseId-dueDate") with a pay/unpay request in flight,
  // so double-clicks can't fire duplicate requests.
  const [pendingPayments, setPendingPayments] = useState(() => new Set());
  const [savingEdit, setSavingEdit] = useState(false);

  // Running balances are server-side now (the ledger). All writes go through the
  // Running balances & arrears panel; the dashboard only reads them for display.

  // Format currency (2 decimal places so line items sum to totals visually)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Filter expenses based on type
  const filteredExpenses = useMemo(() => {
    if (expenseFilter === 'all') return expenses;
    return expenses.filter(exp => exp.expense_type === expenseFilter);
  }, [expenses, expenseFilter]);

  // Filter forecast data based on expense type.
  // For weekly expenses, consolidate the month's actual occurrences (4 or 5 —
  // never a hardcoded ×4) into one entry carrying every occurrence.
  const filteredForecast = useMemo(() => {
    if (!forecast?.forecast) return [];

    return forecast.forecast.map(month => {
      // Filter expenses in each month based on type
      let filteredMonthExpenses = expenseFilter === 'all'
        ? month.expenses
        : month.expenses.filter(exp => exp.expense_type === expenseFilter);

      const weeklyEntries = new Map();
      const consolidatedExpenses = [];

      filteredMonthExpenses.forEach(exp => {
        const expenseDetails = expenses.find(e => e.id === exp.expense_id);
        const isWeekly = expenseDetails?.frequency === 'weekly';

        if (isWeekly) {
          let entry = weeklyEntries.get(exp.expense_id);
          if (!entry) {
            entry = {
              ...exp,
              isWeekly: true,
              weeklyOccurrences: [], // every occurrence this month, in due-date order
              monthlyAmount: 0,      // sum of actual occurrences
            };
            weeklyEntries.set(exp.expense_id, entry);
            consolidatedExpenses.push(entry);
          }
          entry.weeklyOccurrences.push(exp);
          entry.monthlyAmount += exp.amount;
        } else {
          consolidatedExpenses.push(exp);
        }
      });

      // Recalculate totals from actual occurrences
      const filteredTotal = consolidatedExpenses.reduce((sum, exp) => {
        return sum + (exp.isWeekly ? exp.monthlyAmount : exp.amount);
      }, 0);

      return {
        ...month,
        expenses: consolidatedExpenses,
        total: expenseFilter === 'all' ? month.total : filteredTotal,
        displayTotal: filteredTotal
      };
    });
  }, [forecast, expenseFilter, expenses]);

  // Get current month data
  const currentMonthData = filteredForecast[0] || { total: 0, expenses: [] };

  // Toggle one occurrence paid/unpaid via the backend (expense_payments is the
  // single source of truth). Paid state comes back through the forecast reload.
  const handleTogglePaid = async (expenseId, dueDate, amount, expenseName, currentlyPaid) => {
    const paymentKey = `${expenseId}-${dueDate}`;
    if (pendingPayments.has(paymentKey)) return; // request already in flight

    setPendingPayments(prev => new Set(prev).add(paymentKey));
    try {
      if (currentlyPaid) {
        await unmarkExpensePaid(expenseId, dueDate);
      } else {
        // Local calendar date (backend stores a date, not an instant — audit C1).
        await markExpensePaid(expenseId, format(new Date(), 'yyyy-MM-dd'), amount, dueDate);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error updating ${expenseName}`);
    } finally {
      setPendingPayments(prev => {
        const next = new Set(prev);
        next.delete(paymentKey);
        return next;
      });
    }
  };

  // Get running balance for an expense — from the server ledger (read-only here).
  const getRunningBalance = (expenseId) => {
    return balanceByExpense[expenseId]?.balance || 0;
  };

  // Open edit dialog for an expense
  const handleEditExpense = (expense, e) => {
    // Stop propagation to prevent triggering payment toggle
    if (e) {
      e.stopPropagation();
    }
    
    setEditingExpense(expense);
    setEditFormData({
      name: expense.name,
      amount: expense.amount.toString(),
      category_id: expense.category_id,
      expense_type: expense.expense_type,
      frequency: expense.frequency,
      due_date: parseISO(expense.due_date),
      end_date: expense.end_date ? parseISO(expense.end_date) : null,
      notes: expense.notes || '',
      gst_inclusive: expense.gst_inclusive || false,
    });
    setIsEditDialogOpen(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (savingEdit) return; // guard against double-submit

    if (!editFormData.name || !editFormData.amount || !editFormData.category_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSavingEdit(true);
      const data = {
        name: editFormData.name,
        amount: parseFloat(editFormData.amount),
        category_id: editFormData.category_id,
        expense_type: editFormData.expense_type,
        frequency: editFormData.frequency,
        // Send the calendar date as picked, not a UTC instant — toISOString()
        // shifted Sydney dates back a day (audit C1).
        due_date: format(editFormData.due_date, 'yyyy-MM-dd'),
        end_date: editFormData.end_date ? format(editFormData.end_date, 'yyyy-MM-dd') : '',
        notes: editFormData.notes,
        gst_inclusive: editFormData.gst_inclusive,
      };

      // updateExpense already reloads expenses + forecast — no second refresh.
      await updateExpense(editingExpense.id, data);
      toast.success('Expense updated');
      setIsEditDialogOpen(false);
      setEditingExpense(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating expense');
    } finally {
      setSavingEdit(false);
    }
  };

  // Get sorted payment history for display — derived from the backend's
  // per-expense payment records (no local shadow copy).
  const sortedPaymentHistory = useMemo(() => {
    const rows = [];
    expenses.forEach((exp) => {
      (exp.payment_history || []).forEach((p) => {
        rows.push({
          key: `${exp.id}-${p.occurrence_date || p.paid_date}-${p.paid_at}`,
          expenseName: exp.name,
          dueDate: p.occurrence_date || p.paid_date,
          amount: p.amount,
          paidDate: p.paid_at || p.paid_date,
        });
      });
    });
    return rows.sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate));
  }, [expenses]);

  // Calculate remaining balance for current month (unpaid only, filtered by type).
  // Paid state comes from the forecast's per-occurrence records; weekly expenses
  // sum their actual unpaid occurrences (4 or 5 in a month).
  const currentMonthRemaining = useMemo(() => {
    if (!currentMonthData?.expenses) return 0;

    let remaining = 0;

    currentMonthData.expenses.forEach((exp) => {
      if (exp.isWeekly) {
        exp.weeklyOccurrences.forEach((occ) => {
          if (!occ.is_paid) remaining += occ.amount;
        });
      } else if (!exp.is_paid) {
        remaining += exp.amount;
      }
    });

    return remaining;
  }, [currentMonthData]);

  // Calculate total paid this month
  const currentMonthPaid = useMemo(() => {
    if (!currentMonthData?.expenses) return 0;

    let paid = 0;

    currentMonthData.expenses.forEach((exp) => {
      if (exp.isWeekly) {
        exp.weeklyOccurrences.forEach((occ) => {
          if (occ.is_paid) paid += occ.amount;
        });
      } else if (exp.is_paid) {
        paid += exp.amount;
      }
    });

    return paid;
  }, [currentMonthData]);

  // Calculate GST claimable for paid items this month.
  // Only business expenses generate claimable input tax credits (audit M4).
  const currentMonthGSTPaid = useMemo(() => {
    if (!currentMonthData?.expenses) return 0;

    let gstPaid = 0;

    currentMonthData.expenses.forEach((exp) => {
      const expenseDetails = expenses.find(e => e.id === exp.expense_id);
      if (!expenseDetails?.gst_inclusive) return;
      if (expenseDetails.expense_type !== 'business') return;

      if (exp.isWeekly) {
        exp.weeklyOccurrences.forEach((occ) => {
          if (occ.is_paid) gstPaid += occ.amount / 11; // Australian GST rate
        });
      } else if (exp.is_paid) {
        gstPaid += exp.amount / 11;
      }
    });

    return Math.round(gstPaid * 100) / 100;
  }, [currentMonthData, expenses]);

  // Calculate total running balance across all expenses.
  // Only count balances whose expense still exists in THIS account, so orphaned
  // entries (e.g. left over from another profile) never inflate the total.
  const totalRunningBalance = useMemo(() => balances?.total || 0, [balances]);

  // Calculate stats based on filter
  const stats = useMemo(() => {
    if (!forecast?.summary) return { total: 0, avgMonthly: 0, business: 0, personal: 0 };
    
    // Calculate filtered totals
    let total24 = 0;
    filteredForecast.forEach(month => {
      total24 += month.displayTotal || month.total;
    });
    
    const avgMonthly = total24 / 24;
    
    return {
      total: total24,
      avgMonthly: avgMonthly,
      business: forecast.summary.total_business,
      personal: forecast.summary.total_personal,
    };
  }, [forecast, filteredForecast]);

  // Chart data - uses filtered totals
  const chartData = useMemo(() => {
    return filteredForecast.slice(0, 12).map((month) => ({
      month: format(parseISO(`${month.month}-01`), 'MMM'),
      total: month.displayTotal || month.total,
    }));
  }, [filteredForecast]);

  // All expenses for current month
  const currentMonthExpenses = useMemo(() => {
    if (!currentMonthData?.expenses) return [];
    return currentMonthData.expenses
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  }, [currentMonthData]);

  // Calculate monthly remaining (after paid items removed) - handles weekly expenses
  const getMonthlyRemaining = (month) => {
    if (!month?.expenses) return 0;

    let remaining = 0;

    month.expenses.forEach((exp) => {
      if (exp.isWeekly) {
        exp.weeklyOccurrences.forEach((occ) => {
          if (!occ.is_paid) remaining += occ.amount;
        });
      } else if (!exp.is_paid) {
        remaining += exp.amount;
      }
    });

    return remaining;
  };

  // Escape a CSV cell: wrap in quotes and double any embedded quotes.
  const csvCell = (cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`;

  // Export to CSV — every occurrence in each month (weekly expenses get one
  // row per week, not just the first).
  const exportToCSV = () => {
    const headers = ['Expense', 'Type', 'Frequency', 'Amount', 'Due Date', 'Status', 'Paid Date'];
    const rows = [];

    filteredForecast.slice(0, 24).forEach(month => {
      expenses.forEach(expense => {
        if (expenseFilter !== 'all' && expense.expense_type !== expenseFilter) return;

        const entry = month.expenses.find(e => e.expense_id === expense.id);
        if (!entry) return;

        const occurrences = entry.isWeekly ? entry.weeklyOccurrences : [entry];
        occurrences.forEach(occurrence => {
          rows.push([
            expense.name,
            expense.expense_type,
            expense.frequency.replace('_', ' '),
            occurrence.amount,
            format(parseISO(occurrence.due_date), 'dd/MM/yyyy'),
            occurrence.is_paid ? 'Paid' : 'Unpaid',
            occurrence.paid_date ? format(parseISO(occurrence.paid_date), 'dd/MM/yyyy') : ''
          ]);
        });
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(csvCell).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cashflow_forecast_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  // Export payment history to CSV
  const exportHistoryToCSV = () => {
    const headers = ['Expense', 'Due Date', 'Amount', 'Paid Date'];
    const rows = sortedPaymentHistory.map(payment => [
      payment.expenseName,
      format(parseISO(payment.dueDate), 'dd/MM/yyyy'),
      payment.amount,
      format(parseISO(payment.paidDate), 'dd/MM/yyyy HH:mm')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(csvCell).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cashflow_payment_history_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Payment history exported');
  };

  // ---- Monthly expense report (print / save as PDF) -----------------------
  // One row per expense for the chosen month: amount due, paid, unpaid, plus the
  // expense's outstanding running balance from the server ledger. Expenses with
  // an outstanding balance but nothing due this month are listed separately so
  // the report's "Total outstanding" always matches the dashboard.
  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const buildMonthlyReport = (month) => {
    const rows = [];
    let totalDue = 0;
    let totalPaid = 0;

    (month.expenses || [])
      .slice()
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .forEach((exp) => {
        const details = expenses.find((e) => e.id === exp.expense_id);
        const occurrences = exp.isWeekly ? exp.weeklyOccurrences : [exp];
        const due = occurrences.reduce((s, o) => s + o.amount, 0);
        const paid = occurrences.reduce((s, o) => s + (o.is_paid ? o.amount : 0), 0);
        const paidCount = occurrences.filter((o) => o.is_paid).length;
        totalDue += due;
        totalPaid += paid;

        let dueLabel;
        if (exp.isWeekly) {
          dueLabel = `Weekly × ${occurrences.length}`;
        } else {
          dueLabel = format(parseISO(exp.due_date), 'dd MMM yyyy');
        }

        let status;
        if (paidCount === occurrences.length) status = 'Paid';
        else if (paidCount === 0) status = 'Unpaid';
        else status = `${paidCount} of ${occurrences.length} paid`;

        rows.push({
          expenseId: exp.expense_id,
          name: exp.name,
          category: details?.category_name || exp.category?.name || '',
          type: exp.expense_type,
          frequency: (details?.frequency || exp.frequency || '').replace('_', ' '),
          dueLabel,
          due,
          paid,
          unpaid: due - paid,
          status,
          balance: getRunningBalance(exp.expense_id),
        });
      });

    // Arrears carried by expenses that have no occurrence in this month.
    const inMonth = new Set(rows.map((r) => r.expenseId));
    const otherBalances = filteredExpenses
      .filter((e) => !inMonth.has(e.id) && getRunningBalance(e.id) > 0)
      .map((e) => ({
        name: e.name,
        category: e.category_name || '',
        type: e.expense_type,
        balance: getRunningBalance(e.id),
      }));

    const totalOutstanding =
      rows.reduce((s, r) => s + r.balance, 0) + otherBalances.reduce((s, r) => s + r.balance, 0);

    return { rows, otherBalances, totalDue, totalPaid, totalUnpaid: totalDue - totalPaid, totalOutstanding };
  };

  const printReport = () => {
    const month = filteredForecast[Number(reportMonth)] || filteredForecast[0];
    if (!month) {
      toast.error('No forecast data to report on');
      return;
    }

    const report = buildMonthlyReport(month);
    const monthLabel = format(parseISO(`${month.month}-01`), 'MMMM yyyy');
    const filterLabel = expenseFilter === 'all' ? 'All expenses' : `${expenseFilter === 'business' ? 'Business' : 'Personal'} expenses only`;
    const money = (n) => escapeHtml(formatCurrency(n));
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

    const bodyRows = report.rows.length === 0
      ? `<tr><td colspan="9" class="empty">No expenses due in ${escapeHtml(monthLabel)}</td></tr>`
      : report.rows.map((r) => `
        <tr class="${r.status === 'Paid' ? 'paid' : ''}">
          <td>${escapeHtml(r.name)}</td>
          <td>${escapeHtml(r.category)}</td>
          <td>${escapeHtml(cap(r.type))}</td>
          <td>${escapeHtml(cap(r.frequency))}</td>
          <td>${escapeHtml(r.dueLabel)}</td>
          <td class="num">${money(r.due)}</td>
          <td class="num">${money(r.paid)}</td>
          <td class="num">${r.unpaid > 0 ? money(r.unpaid) : '–'}</td>
          <td class="num ${r.balance > 0 ? 'arrears' : ''}">${r.balance > 0 ? money(r.balance) : '–'}</td>
        </tr>`).join('');

    const otherSection = report.otherBalances.length === 0 ? '' : `
      <h2>Other outstanding balances</h2>
      <p class="muted">Expenses with an outstanding balance but nothing due in ${escapeHtml(monthLabel)}.</p>
      <table>
        <thead><tr><th>Expense</th><th>Category</th><th>Type</th><th class="num">Outstanding balance</th></tr></thead>
        <tbody>
          ${report.otherBalances.map((r) => `
            <tr>
              <td>${escapeHtml(r.name)}</td>
              <td>${escapeHtml(r.category)}</td>
              <td>${escapeHtml(cap(r.type))}</td>
              <td class="num arrears">${money(r.balance)}</td>
            </tr>`).join('')}
        </tbody>
      </table>`;

    const html = `<!DOCTYPE html>
<html lang="en-AU">
<head>
<meta charset="utf-8">
<title>${escapeHtml(activeCashflow?.name || 'Cashflow')} – Expense report ${escapeHtml(monthLabel)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #1c1917; margin: 0; padding: 28px 32px; font-size: 12px; }
  h1 { color: #021d49; font-size: 22px; margin: 0 0 4px; }
  h2 { color: #021d49; font-size: 15px; margin: 28px 0 6px; }
  .meta { color: #57534e; margin: 0 0 18px; line-height: 1.5; }
  .muted { color: #78716c; margin: 0 0 8px; }
  .summary { display: flex; gap: 12px; margin: 0 0 20px; }
  .summary div { flex: 1; border: 1px solid #e7e5e4; border-radius: 4px; padding: 10px 12px; }
  .summary .label { display: block; color: #78716c; font-size: 11px; margin-bottom: 4px; }
  .summary .value { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .summary .arrears-card { background: #fef2f2; border-color: #fecaca; }
  .summary .arrears-card .value { color: #b91c1c; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #e7e5e4; padding: 7px 8px; text-align: left; vertical-align: top; }
  th { background: #f5f5f4; color: #44403c; font-size: 11px; text-transform: uppercase; letter-spacing: .03em; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tr.paid td { color: #57534e; }
  table td.arrears { color: #b91c1c; font-weight: 600; }
  tfoot td { font-weight: 700; background: #f5f5f4; border-top: 2px solid #d6d3d1; }
  td.empty { text-align: center; color: #78716c; padding: 24px; }
  .foot { margin-top: 24px; color: #a8a29e; font-size: 10px; }
  @page { size: A4 landscape; margin: 14mm; }
  @media print { body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <h1>Expense report – ${escapeHtml(monthLabel)}</h1>
  <p class="meta">
    Cashflow: <strong>${escapeHtml(activeCashflow?.name || '')}</strong> &nbsp;·&nbsp; ${escapeHtml(filterLabel)}<br>
    Generated ${escapeHtml(format(new Date(), 'dd MMMM yyyy, HH:mm'))}
  </p>

  <div class="summary">
    <div><span class="label">Due in ${escapeHtml(monthLabel)}</span><span class="value">${money(report.totalDue)}</span></div>
    <div><span class="label">Paid</span><span class="value">${money(report.totalPaid)}</span></div>
    <div><span class="label">Still to pay this month</span><span class="value">${money(report.totalUnpaid)}</span></div>
    <div class="${report.totalOutstanding > 0 ? 'arrears-card' : ''}"><span class="label">Total outstanding balance</span><span class="value">${money(report.totalOutstanding)}</span></div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Expense</th>
        <th>Category</th>
        <th>Type</th>
        <th>Frequency</th>
        <th>Due</th>
        <th class="num">Amount</th>
        <th class="num">Paid</th>
        <th class="num">Unpaid</th>
        <th class="num">Outstanding balance</th>
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="5">Total</td>
        <td class="num">${money(report.totalDue)}</td>
        <td class="num">${money(report.totalPaid)}</td>
        <td class="num">${money(report.totalUnpaid)}</td>
        <td class="num">${money(report.rows.reduce((s, r) => s + r.balance, 0))}</td>
      </tr>
    </tfoot>
  </table>
  ${otherSection}

  <p class="foot">Outstanding balance = unpaid amounts carried forward from previous months (running-balance ledger), less any payments recorded against them.</p>
  <script>
    window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 200); });
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups for this site to print the report');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  if (loading || !activeCashflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-stone-500 mt-1">
            {activeCashflow.name} - 24 month forecast
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={expenseFilter} onValueChange={setExpenseFilter}>
            <SelectTrigger className="w-40 rounded-[4px]" data-testid="expense-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Expenses</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
            </SelectContent>
          </Select>

          {/* Payment History Button */}
          <Dialog open={showHistory} onOpenChange={setShowHistory}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-[4px]" data-testid="history-btn">
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <span>Payment History</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={exportHistoryToCSV} className="rounded-[4px]">
                      <Download className="w-4 h-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto mt-4">
                {sortedPaymentHistory.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                    <p>No payment history yet</p>
                    <p className="text-sm">Mark payments as paid to track history</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sortedPaymentHistory.map((payment) => (
                      <div 
                        key={payment.key}
                        className="flex items-center justify-between p-3 bg-stone-50 rounded-[4px]"
                      >
                        <div>
                          <p className="font-medium text-stone-900">{payment.expenseName}</p>
                          <p className="text-xs text-stone-500">
                            Due: {format(parseISO(payment.dueDate), 'dd MMM yyyy')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-medium text-green-600">{formatCurrency(payment.amount)}</p>
                          <p className="text-xs text-stone-500">
                            Paid: {format(parseISO(payment.paidDate), 'dd MMM yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Export Button */}
          <Dialog open={showExport} onOpenChange={setShowExport}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-[4px]" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Export Report
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="p-3 border border-stone-200 rounded-[4px] space-y-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Monthly expense report</p>
                      <p className="text-xs text-stone-500">Expenses due, paid, unpaid and outstanding balance</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={reportMonth} onValueChange={setReportMonth}>
                      <SelectTrigger className="flex-1 rounded-[4px]" data-testid="report-month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredForecast.slice(0, 24).map((month, idx) => (
                          <SelectItem key={month.month} value={String(idx)}>
                            {format(parseISO(`${month.month}-01`), 'MMMM yyyy')}{idx === 0 ? ' (this month)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => { printReport(); setShowExport(false); }}
                      className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
                      data-testid="print-report-btn"
                    >
                      Print / PDF
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={() => { exportToCSV(); setShowExport(false); }}
                  className="w-full justify-start bg-white border border-stone-200 text-stone-900 hover:bg-stone-50 rounded-[4px]"
                >
                  <FileText className="w-5 h-5 mr-3 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">Export to CSV</p>
                    <p className="text-xs text-stone-500">Download 24-month forecast as spreadsheet</p>
                  </div>
                </Button>
                <Button 
                  onClick={() => { exportHistoryToCSV(); setShowExport(false); }}
                  className="w-full justify-start bg-white border border-stone-200 text-stone-900 hover:bg-stone-50 rounded-[4px]"
                >
                  <History className="w-5 h-5 mr-3 text-purple-600" />
                  <div className="text-left">
                    <p className="font-medium">Export Payment History</p>
                    <p className="text-xs text-stone-500">Download all paid transactions</p>
                  </div>
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            onClick={() => navigate('/expenses')}
            className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Stats Cards - Row 1: Monthly Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">This Month Total</p>
                <p className="text-xl font-bold text-stone-900 font-mono mt-1" data-testid="this-month-total">
                  {formatCurrency(currentMonthData.total)}
                </p>
              </div>
              <div className="w-9 h-9 bg-stone-100 rounded-[4px] flex items-center justify-center">
                <Calendar className="w-4 h-4 text-stone-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-600 border-none rounded-lg shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-green-100">Total Paid</p>
                <p className="text-xl font-bold text-white font-mono mt-1" data-testid="total-paid">
                  {formatCurrency(currentMonthPaid)}
                </p>
              </div>
              <div className="w-9 h-9 bg-white/20 rounded-[4px] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#021d49] border-none rounded-lg shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-200">Remaining to Pay</p>
                <p className="text-xl font-bold text-white font-mono mt-1" data-testid="remaining-to-pay">
                  {formatCurrency(currentMonthRemaining)}
                </p>
              </div>
              <div className="w-9 h-9 bg-white/10 rounded-[4px] flex items-center justify-center">
                <Wallet className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Running Balance Card - RED when > 0 */}
        <Card className={`${totalRunningBalance > 0 ? 'bg-red-600' : 'bg-stone-100'} border-none rounded-lg shadow-sm`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm ${totalRunningBalance > 0 ? 'text-red-100' : 'text-stone-500'}`}>Running Balance</p>
                <p className={`text-xl font-bold font-mono mt-1 ${totalRunningBalance > 0 ? 'text-white' : 'text-stone-400'}`} data-testid="running-balance-total">
                  {formatCurrency(totalRunningBalance)}
                </p>
                {totalRunningBalance > 0 && (
                  <p className="text-xs text-red-200 mt-1">Overdue amounts</p>
                )}
              </div>
              <div className={`w-9 h-9 ${totalRunningBalance > 0 ? 'bg-white/20' : 'bg-stone-200'} rounded-[4px] flex items-center justify-center`}>
                <AlertTriangle className={`w-4 h-4 ${totalRunningBalance > 0 ? 'text-white' : 'text-stone-400'}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-teal-600 border-none rounded-lg shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-teal-100">GST Claimable</p>
                <p className="text-xl font-bold text-white font-mono mt-1" data-testid="gst-claimable">
                  {formatCurrency(currentMonthGSTPaid)}
                </p>
                <p className="text-xs text-teal-200 mt-1">From paid items</p>
              </div>
              <div className="w-9 h-9 bg-white/20 rounded-[4px] flex items-center justify-center">
                <Receipt className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards - Row 2: Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">Monthly Average</p>
                <p className="text-2xl font-bold text-stone-900 font-mono mt-1" data-testid="monthly-average">
                  {formatCurrency(stats.avgMonthly)}
                </p>
              </div>
              <div className="w-10 h-10 bg-stone-100 rounded-[4px] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">Business (24mo)</p>
                <p className="text-2xl font-bold text-blue-700 font-mono mt-1" data-testid="business-total">
                  {formatCurrency(stats.business)}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-50 rounded-[4px] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">Personal (24mo)</p>
                <p className="text-2xl font-bold text-pink-700 font-mono mt-1" data-testid="personal-total">
                  {formatCurrency(stats.personal)}
                </p>
              </div>
              <div className="w-10 h-10 bg-pink-50 rounded-[4px] flex items-center justify-center">
                <User className="w-5 h-5 text-pink-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart - Full Width */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            12 Month Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="navyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#021d49" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#021d49" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="month" 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fill: '#78716C', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fill: '#78716C', fontSize: 12 }}
                  tickFormatter={(value) => `$${value / 1000}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#021d49' }}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E7E5E4',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#021d49" 
                  strokeWidth={2}
                  fill="url(#navyGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* This Month Expenses - 3 Column Grid with Running Balance */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-[#021d49] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Clock className="w-5 h-5 text-stone-400" />
            This Month
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {currentMonthExpenses.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>No expenses this month</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentMonthExpenses.map((exp) => {
                const expenseDetails = expenses.find(e => e.id === exp.expense_id);
                const isWeekly = expenseDetails?.frequency === 'weekly';
                const runningBalance = getRunningBalance(exp.expense_id);
                const hasOverdue = runningBalance > 0;
                
                if (isWeekly) {
                  // Weekly expense - one button per actual occurrence (4 or 5)
                  const weekOccurrences = exp.weeklyOccurrences || [];
                  const unpaidAmount = weekOccurrences.reduce(
                    (sum, occ) => sum + (occ.is_paid ? 0 : occ.amount), 0
                  );
                  const allPaid = weekOccurrences.length > 0 && weekOccurrences.every(occ => occ.is_paid);

                  return (
                    <div 
                      key={`${exp.expense_id}-${exp.due_date}`}
                      className={`p-3 rounded-[4px] ${allPaid && !hasOverdue ? 'bg-green-50' : hasOverdue ? 'bg-red-50' : 'bg-stone-50'} group`}
                    >
                      {/* Running balance (read-only; managed in the arrears panel) */}
                      {hasOverdue && (
                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-red-200">
                          <span className="text-xs font-medium text-red-600">Running balance:</span>
                          <span className="text-sm font-mono font-bold text-red-600">{formatCurrency(runningBalance)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <div
                          className="flex-1 min-w-0 cursor-pointer hover:opacity-80"
                          onClick={() => expenseDetails && handleEditExpense(expenseDetails)}
                          title="Click to edit"
                        >
                          <p className={`text-sm font-medium ${allPaid && !hasOverdue ? 'text-green-700' : hasOverdue ? 'text-red-700' : 'text-stone-900'} flex items-center gap-1`}>
                            {exp.name}
                            <Pencil className="w-3 h-3 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </p>
                          <p className="text-xs text-stone-500">
                            {formatCurrency(exp.amount)}/wk = {formatCurrency(exp.monthlyAmount)}/mo
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-mono font-medium ${allPaid && !hasOverdue ? 'text-green-600' : 'text-stone-900'}`}>
                            {formatCurrency(unpaidAmount)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {weekOccurrences.map((occ, idx) => {
                          const week = idx + 1;
                          const pending = pendingPayments.has(`${exp.expense_id}-${occ.due_date}`);
                          return (
                            <button
                              key={occ.due_date}
                              onClick={() => handleTogglePaid(exp.expense_id, occ.due_date, occ.amount, exp.name, occ.is_paid)}
                              disabled={pending}
                              className={`flex-1 py-1 px-1.5 text-xs font-medium rounded-[4px] border transition-colors ${
                                occ.is_paid
                                  ? 'bg-green-600 text-white border-green-600'
                                  : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
                              } ${pending ? 'opacity-50 cursor-wait' : ''}`}
                              title={`Week ${week} — due ${format(parseISO(occ.due_date), 'MMM d')}`}
                              data-testid={`week-${week}-${exp.expense_id}`}
                            >
                              W{week}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                // Regular expense - show single checkbox (paid state from forecast)
                const isPaid = !!exp.is_paid;
                const payPending = pendingPayments.has(`${exp.expense_id}-${exp.due_date}`);
                return (
                  <div 
                    key={`${exp.expense_id}-${exp.due_date}`}
                    className={`p-3 rounded-[4px] transition-colors ${isPaid && !hasOverdue ? 'bg-green-50' : hasOverdue ? 'bg-red-50' : 'bg-stone-50 hover:bg-stone-100'} group`}
                  >
                    {/* Running balance (read-only; managed in the arrears panel) */}
                    {hasOverdue && (
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-red-200">
                        <span className="text-xs font-medium text-red-600">Running balance:</span>
                        <span className="text-sm font-mono font-bold text-red-600">{formatCurrency(runningBalance)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isPaid}
                        disabled={payPending}
                        onCheckedChange={() => handleTogglePaid(exp.expense_id, exp.due_date, exp.amount, exp.name, isPaid)}
                        className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                        data-testid={`pay-checkbox-${exp.expense_id}`}
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => expenseDetails && handleEditExpense(expenseDetails)}
                        title="Click to edit"
                      >
                        <p className={`text-sm font-medium ${isPaid && !hasOverdue ? 'text-green-700 line-through' : hasOverdue ? 'text-red-700' : 'text-stone-900'} flex items-center gap-1`}>
                          {exp.name}
                          <Pencil className="w-3 h-3 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </p>
                        <p className="text-xs text-stone-500">
                          Due: {format(parseISO(exp.due_date), 'MMM d')}
                        </p>
                      </div>
                      <p className={`text-sm font-mono font-medium ${isPaid && !hasOverdue ? 'text-green-600 line-through' : 'text-stone-900'}`}>
                        {formatCurrency(exp.amount)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 24 Month Forecast Table */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <CardHeader className="border-b border-stone-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              24 Month Forecast
            </CardTitle>
            <p className="text-sm text-stone-500">Check payments to mark as paid</p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <div className="min-w-[1400px]" id="forecast-table">
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-stone-50 z-10 min-w-[200px]">Expense</th>
                    <th className="min-w-[100px] bg-red-50 text-red-700">Running Balance</th>
                    <th className="min-w-[80px]">Type</th>
                    <th className="min-w-[80px]">Frequency</th>
                    {filteredForecast.slice(0, 24).map((month) => (
                      <th key={month.month} className="month-header text-left min-w-[120px]">
                        {format(parseISO(`${month.month}-01`), 'MMM yyyy')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((expense) => {
                    const runningBalance = getRunningBalance(expense.id);
                    const hasOverdue = runningBalance > 0;
                    
                    return (
                    <tr key={expense.id} className={`${!expense.is_active ? 'opacity-50' : ''} ${hasOverdue ? 'bg-red-50/30' : ''}`}>
                      <td className="sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={expense.is_active}
                            onCheckedChange={() => toggleExpense(expense.id)}
                            className="data-[state=checked]:bg-[#021d49]"
                            data-testid={`toggle-expense-${expense.id}`}
                          />
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:opacity-80 group"
                            onClick={() => handleEditExpense(expense)}
                            title="Click to edit"
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: expense.category?.color || '#78716C' }}
                            />
                            <span className={`font-medium flex items-center gap-1 ${hasOverdue ? 'text-red-700' : ''}`}>
                              {expense.name}
                              <Pencil className="w-3 h-3 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </span>
                          </div>
                        </div>
                      </td>
                      {/* Arrears Column — ledger-backed, adjustable inline */}
                      <td className={`${hasOverdue ? 'bg-red-50' : ''}`}>
                        <ArrearsCell
                          expense={expense}
                          info={balanceByExpense[expense.id]}
                          onRecord={recordBalanceEntry}
                        />
                      </td>
                      <td>
                        <Badge 
                          variant="secondary"
                          className={expense.expense_type === 'business' ? 'type-business' : 'type-personal'}
                        >
                          {expense.expense_type}
                        </Badge>
                      </td>
                      <td>
                        <Badge 
                          variant="secondary"
                          className={`freq-${expense.frequency.replace('_', '-')}`}
                        >
                          {expense.frequency.replace('_', ' ')}
                        </Badge>
                      </td>
                      {filteredForecast.slice(0, 24).map((month) => {
                        const occurrence = month.expenses.find(e => e.expense_id === expense.id);
                        if (!occurrence) {
                          return (
                            <td key={month.month} className="text-left font-mono">
                              <span className="text-stone-300">-</span>
                            </td>
                          );
                        }
                        
                        const isWeekly = expense.frequency === 'weekly';

                        if (isWeekly) {
                          // Weekly expense - one button per actual occurrence (4 or 5).
                          // The ::before pseudo-element pads the tap target to ~44px
                          // tall without growing the 20px visual square (mobile).
                          const weekOccurrences = occurrence.weeklyOccurrences || [];
                          const allPaid = weekOccurrences.length > 0 && weekOccurrences.every(occ => occ.is_paid);

                          return (
                            <td key={month.month} className="text-left">
                              <div className="space-y-1">
                                <div className="flex gap-2">
                                  {weekOccurrences.map((occ, idx) => {
                                    const week = idx + 1;
                                    const pending = pendingPayments.has(`${expense.id}-${occ.due_date}`);
                                    return (
                                      <button
                                        key={occ.due_date}
                                        onClick={() => handleTogglePaid(expense.id, occ.due_date, occ.amount, expense.name, occ.is_paid)}
                                        disabled={pending}
                                        className={`relative w-5 h-5 text-[10px] font-medium rounded-sm border transition-colors before:content-[''] before:absolute before:-inset-y-3 before:-inset-x-1 ${
                                          occ.is_paid
                                            ? 'bg-green-600 text-white border-green-600'
                                            : 'bg-white text-stone-500 border-stone-300 hover:border-stone-400'
                                        } ${pending ? 'opacity-50 cursor-wait' : ''}`}
                                        title={`Week ${week} — due ${format(parseISO(occ.due_date), 'MMM d')}`}
                                        data-testid={`pay-${expense.id}-${month.month}-w${week}`}
                                      >
                                        {week}
                                      </button>
                                    );
                                  })}
                                </div>
                                <span className={`font-mono text-xs ${allPaid ? 'text-green-600' : 'text-stone-900'}`}>
                                  {formatCurrency(occurrence.monthlyAmount)}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Regular expense - show checkbox (paid state from forecast)
                        const isPaid = !!occurrence.is_paid;
                        const payPending = pendingPayments.has(`${expense.id}-${occurrence.due_date}`);

                        return (
                          <td key={month.month} className="text-left">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={isPaid}
                                disabled={payPending}
                                onCheckedChange={() => handleTogglePaid(expense.id, occurrence.due_date, occurrence.amount, expense.name, isPaid)}
                                className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                data-testid={`pay-${expense.id}-${month.month}`}
                              />
                              <span className={`font-mono ${isPaid ? 'text-green-600 line-through' : 'text-stone-900'}`}>
                                {formatCurrency(occurrence.amount)}
                              </span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                  })}
                  {/* Running Balance Total Row */}
                  <tr className="bg-red-50 font-semibold border-t-2 border-red-200">
                    <td className="sticky left-0 bg-red-50 z-10 text-red-700">Running Balance Total</td>
                    <td className={`${totalRunningBalance > 0 ? 'bg-red-100 text-red-700' : 'bg-red-50 text-stone-400'} font-mono font-bold`}>
                      {totalRunningBalance > 0 ? formatCurrency(totalRunningBalance) : '-'}
                    </td>
                    <td className="bg-red-50"></td>
                    <td className="bg-red-50"></td>
                    {filteredForecast.slice(0, 24).map((month) => (
                      <td key={month.month} className="bg-red-50"></td>
                    ))}
                  </tr>
                  {/* Remaining Row */}
                  <tr className="remaining-row bg-[#021d49] text-white font-semibold">
                    <td className="sticky left-0 bg-[#021d49] z-10">Remaining to Pay</td>
                    <td className="bg-[#021d49]"></td>
                    <td></td>
                    <td></td>
                    {filteredForecast.slice(0, 24).map((month) => (
                      <td key={month.month} className="text-left font-mono">
                        {formatCurrency(getMonthlyRemaining(month))}
                      </td>
                    ))}
                  </tr>
                  {/* Totals Row */}
                  <tr className="total-row bg-stone-100 font-semibold">
                    <td className="sticky left-0 bg-stone-100 z-10">Monthly Total</td>
                    <td className="bg-stone-100"></td>
                    <td></td>
                    <td></td>
                    {filteredForecast.slice(0, 24).map((month) => (
                      <td key={month.month} className="text-left font-mono text-stone-900">
                        {formatCurrency(month.displayTotal || month.total)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit Expense Dialog (shared with the Expenses page) */}
      <ExpenseFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        title="Edit Expense"
        submitLabel="Save Changes"
        formData={editFormData}
        setFormData={setEditFormData}
        categories={categories}
        onSubmit={handleEditSubmit}
        isSubmitting={savingEdit}
        testIdPrefix="edit-expense"
      />
    </div>
  );
}
